import { DownDetectorMapping, useORPCClient } from "Core/oRPC";
import { GetScopeSearchRequest, GetScopeValuesRequest, ScopeItem, TopologyService } from "../Scopes.types";

import { Types } from "Shared/Utils";
import { useCallback } from "react";
import { useLocalObservable } from "mobx-react";

export const useDownDetectorTopologyService = (): TopologyService => {
    const localStore = useLocalObservable(() => ({
        hasFetchedMappings: false,
        cachedMappings: new Array<DownDetectorMapping>(),
        workloadList: new Array<string>(),
        workloadToRegionMap: new Map<string, string[]>(),
        regionToCountryMap: new Map<string, string[]>()
    }));
    const { healthApi } = useORPCClient();
    const fetchMappings = useCallback(async () => {
        if (!localStore.hasFetchedMappings) {
            localStore.cachedMappings = await healthApi.getDownDetectorMappings();
            localStore.workloadList = [...new Set(localStore.cachedMappings.map((m) => m.WorkloadName).filter(Types.nonNullable))];
            localStore.workloadList.forEach((w) => {
                const regions = localStore.cachedMappings
                    .filter((m) => m.WorkloadName === w)
                    .map((r) => r.Region)
                    .filter(Types.nonNullable);
                const uniqueRegions: string[] = [...new Set(regions)];
                localStore.workloadToRegionMap.set(w, uniqueRegions.sort());
            });
            const regionList = [...new Set(localStore.cachedMappings.map((m) => m.Region).filter(Types.nonNullable))];
            regionList.forEach((r) => {
                const countries = localStore.cachedMappings
                    .filter((m) => m.Region === r)
                    .map((c) => c.Country)
                    .filter(Types.nonNullable);
                const uniqueCountries: string[] = [...new Set(countries)];
                localStore.regionToCountryMap.set(r, uniqueCountries.sort());
            });
            localStore.hasFetchedMappings = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localStore.hasFetchedMappings]);

    return {
        getScopeValueOptions: async (request: GetScopeValuesRequest): Promise<ScopeItem[]> => {
            const currentScopeTarget = request.currentScopeTarget;
            await fetchMappings();
            switch (currentScopeTarget.ScopeType.toLowerCase()) {
                case "workloadname":
                    return localStore.workloadList.map((workload) => ({
                        ScopeType: "WorkloadName",
                        DisplayName: "Product",
                        Value: workload
                    }));
                case "region":
                    const selectedWorkload = request.selectedScopesSoFar?.find((scope) => scope.ScopeType?.toLowerCase() === "workloadname");
                    const regions = localStore.workloadToRegionMap.get(selectedWorkload?.Value || "") || [];
                    return regions.map((region) => ({
                        ScopeType: "Region",
                        Value: region
                    }));
                case "country":
                    const selectedRegion = request.selectedScopesSoFar?.find((scope) => scope.ScopeType?.toLowerCase() === "region");
                    const countries = localStore.regionToCountryMap.get(selectedRegion?.Value || "") || [];
                    return countries.map((region) => ({
                        ScopeType: "Country",
                        Value: region
                    }));
                default:
                    break;
            }
            return [];
        },
        getScopeSearch: (request: GetScopeSearchRequest): ScopeItem[] | Promise<ScopeItem[]> => [{ ScopeType: "", Value: "" }]
    };
};
